// formPrintBuilder.js
//
// Same job as formPdfBuilder.js — render a printable snapshot of a dynamic form
// submission — but instead of building a .pdf file with jsPDF, this renders an
// HTML document into a new browser tab and opens the native print dialog
// (Ctrl/Cmd+P), the same one shown in the reference screenshot ("Save as PDF" /
// "Print" / pick a printer). No jsPDF/jspdf-autotable dependency at all.
//
// Usage:
//   import { printForm } from "./formPrintBuilder"
//
//   await printForm({
//     formItem,           // { title_key, slug, schema: { fields } }
//     formValues,         // current form state / submission.data
//     meta: {
//       logoUrl: "/assets/wehomecare-logo.png", // same-origin path works as an <img src>
//       appName: "WeHomeCare Portal",  // optional override; falls back to
//                                      // localStorage.getItem("app_name") if omitted
//       orgName: "WeHomeCare",
//       patientName: "James Skidmore",
//       filledByName: "admin",
//       filledDate: "19 Feb 2026, 13:05",
//       lastUpdatedDate: "05 Mar 2026, 10:34",
//       refNumber: "WHC-9382-9314-8ABR-20260219-0735",
//     },
//     translate: voiceiiTags, // optional i18n fn, defaults to identity
//   })
//
// Notes:
// - `appName` is shown as a small line above everything else in the header.
//   If you don't pass it explicitly, it's read from
//   localStorage.getItem("app_name") — set that key wherever your app already
//   stores the tenant/app name, and every printed form will pick it up for free.
// - The print window is opened synchronously with window.open() so popup
//   blockers don't kick in — do NOT put an `await` before this call inside the
//   click handler.

export const OTHER_OPTION_VALUE = "__other__"

const t = (translate, key) => (typeof translate === "function" ? translate(key) : key)

const getOtherFieldKey = (fieldKey) => `${fieldKey}__other`

const esc = (value) =>
    String(value ?? "").replace(
        /[&<>"']/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
    )

const getFieldOptions = (field) =>
    (field?.options || [])
        .map((opt) =>
            typeof opt === "string"
                ? { value: opt, label: opt }
                : { value: opt?.value, label: opt?.label_key || opt?.label || opt?.value },
        )
        .filter((opt) => opt.value !== undefined && opt.value !== null && opt.value !== "")

const getFormattedDateValue = (value) => {
    if (!value) return "-"
    const stringValue = String(value).trim()

    if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
        const [year, month, day] = stringValue.split("-")
        return `${day}/${month}/${year}`
    }

    const parsedDate = new Date(stringValue)
    if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString("en-GB")
    }

    return stringValue || "-"
}

const getPrintableFileName = (fileValue) => {
    if (!fileValue) return "-"
    if (typeof File !== "undefined" && fileValue instanceof File) {
        return fileValue.name || "file"
    }
    if (typeof fileValue === "string") {
        const fileName = fileValue.split("/").pop()?.split("?")[0] || fileValue
        try {
            return decodeURIComponent(fileName)
        } catch {
            return fileName
        }
    }
    if (fileValue?.name) return fileValue.name
    const raw =
        fileValue?.url || fileValue?.path || fileValue?.file_path || fileValue?.filePath || fileValue?.location
    if (raw) {
        const fileName = String(raw).split("/").pop()?.split("?")[0] || "file"
        try {
            return decodeURIComponent(fileName)
        } catch {
            return fileName
        }
    }
    return "file"
}

/** Builds the { options, selectedValues } pair for any choice-type field, folding in "Other". */
const buildChecklistData = (field, formValues, translate) => {
    const rawValue = formValues?.[field.key]
    const otherValue = String(formValues?.[getOtherFieldKey(field.key)] || "").trim()
    const isMulti = field.type === "multi-select"

    const options = getFieldOptions(field)
    const selectedValues = isMulti
        ? Array.isArray(rawValue)
            ? rawValue
            : []
        : [rawValue].filter((v) => v !== undefined && v !== null && v !== "")

    if (field.allow_other) {
        const isOtherSelected = selectedValues.includes(OTHER_OPTION_VALUE)
        options.push({
            value: OTHER_OPTION_VALUE,
            label: isOtherSelected && otherValue ? `${t(translate, "Other")}: ${otherValue}` : t(translate, "Other"),
        })
    }

    return { options, selectedValues }
}

const buildCheckboxFieldData = (field, formValues, translate) => {
    const value = !!formValues?.[field.key]
    return {
        options: [{ value: true, label: field.title_key || field.label_key || t(translate, "Yes") }],
        selectedValues: value ? [true] : [],
    }
}

const renderChecklistHtml = (options, selectedValues, { vertical = false } = {}) => {
    if (!options.length) return `<span class="e-print-value">-</span>`
    return `<div class="e-print-checklist${vertical ? " e-print-checklist-vertical" : ""}">${options
        .map((opt) => {
            const checked = selectedValues.includes(opt.value)
            return `<label class="e-print-check-item"><span class="e-print-checkbox${
                checked ? " is-checked" : ""
            }">${checked ? "&#10003;" : ""}</span><span>${esc(opt.label)}</span></label>`
        })
        .join("")}</div>`
}

const renderRow = (label, valueHtml) => `
    <div class="e-print-row">
        <div class="e-print-label">${esc(label)}</div>
        <div class="e-print-value-col">${valueHtml}</div>
    </div>`

const renderFieldRows = (formItem, formValues, translate) => {
    const fields = formItem?.schema?.fields || []
    let html = ""

    fields.forEach((field) => {
        if (!field) return

        if (field.type === "section-header") {
            html += `<div class="e-print-section">
                <div class="e-print-section-title">${esc(field.label_key || t(translate, "Section"))}</div>
                ${field.description_key ? `<div class="e-print-section-desc">${esc(field.description_key)}</div>` : ""}
            </div>`
            return
        }

        if (!field.key) return

        if (field.type === "select" || field.type === "radio") {
            const { options, selectedValues } = buildChecklistData(field, formValues, translate)
            html += renderRow(field.label_key || field.key, renderChecklistHtml(options, selectedValues))
            return
        }

        if (field.type === "multi-select") {
            const { options, selectedValues } = buildChecklistData(field, formValues, translate)
            html += renderRow(
                field.label_key || field.key,
                renderChecklistHtml(options, selectedValues, { vertical: true }),
            )
            return
        }

        if (field.type === "checkbox") {
            const { options, selectedValues } = buildCheckboxFieldData(field, formValues, translate)
            html += renderRow(field.label_key || "", renderChecklistHtml(options, selectedValues))
            return
        }

        if (field.type === "radio-grid") {
            const gridValue = formValues?.[field.key] || {}
            const rowsHtml = (field.rows || [])
                .map((row) => {
                    const selectedValue = gridValue?.[row.key]
                    const selectedColumn = (field.column_headers || []).find((c) => c.value === selectedValue)
                    const displayValue = selectedColumn?.label_key || selectedColumn?.value || selectedValue || "-"
                    return `<tr><td>${esc(row.label_key || row.key)}</td><td>${esc(displayValue)}</td></tr>`
                })
                .join("")

            html += `<div class="e-print-field-block">
                <div class="e-print-field-title">${esc(field.label_key || field.key)}</div>
                <table class="e-print-grid-table">
                    <thead><tr><th>${esc(t(translate, "Question"))}</th><th>${esc(
                        t(translate, "Selected Value"),
                    )}</th></tr></thead>
                    <tbody>${rowsHtml || `<tr><td colspan="2">${esc(t(translate, "No records found"))}</td></tr>`}</tbody>
                </table>
            </div>`
            return
        }

        if (field.type === "date") {
            html += renderRow(
                field.label_key || field.key,
                `<span class="e-print-value">${esc(getFormattedDateValue(formValues?.[field.key]))}</span>`,
            )
            return
        }

        if (field.type === "file" || field.type === "files" || field.type === "image" || field.type === "images") {
            const raw = formValues?.[field.key]
            const fileList = Array.isArray(raw) ? raw : raw ? [raw] : []
            const text = fileList.length ? fileList.map(getPrintableFileName).join(", ") : "-"
            html += renderRow(field.label_key || field.key, `<span class="e-print-value">${esc(text)}</span>`)
            return
        }

        if (field.type === "form-reference-number") {
            const value = formValues?.[field.key] || field.default_value || field.placeholder_key || "-"
            html += renderRow(
                field.label_key || t(translate, "Form Ref No."),
                `<span class="e-print-value">${esc(value)}</span>`,
            )
            return
        }

        // text, textarea, number, computed, and any other simple scalar field
        const value = formValues?.[field.key]
        const text =
            value === null || value === undefined || value === ""
                ? "-"
                : Array.isArray(value)
                  ? value.join(", ") || "-"
                  : typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value)

        html += renderRow(field.label_key || field.title_key || field.key, `<span class="e-print-value">${esc(text)}</span>`)
    })

    return html
}

const PRINT_STYLES = `
    * { box-sizing: border-box; }
    html { font-size: 16px; }
    body { font-family: Arial, Helvetica, sans-serif; color: #141414; margin: 0; padding: 32px 42px; font-size: 1rem; }
    .e-print-header { text-align: center; margin-bottom: 18px; }
    .e-print-app-name { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
    .e-print-logo { max-height: 50px; margin-bottom: 8px; filter: grayscale(100%); }
    .e-print-org-name { font-size: 1.15rem; font-weight: 700; margin-bottom: 3px; }
    .e-print-title { font-size: 1.5rem; font-weight: 700; margin: 5px 0; }
    .e-print-patient { font-size: 1rem; margin-top: 3px; }
    .e-print-date { font-size: 0.85rem; color: #6b7280; margin-top: 3px; }
    .e-print-divider { border: none; border-top: 2px solid #141414; margin: 12px 0 18px; }
    .e-print-row { display: flex; gap: 16px; padding: 10px 0; border-bottom: 1px solid #e4e4e4; }
    .e-print-label { width: 34%; font-weight: 600; font-size: 0.875rem; }
    .e-print-value-col { width: 66%; font-size: 1rem; }
    .e-print-value { font-size: 1rem; white-space: pre-wrap; }
    .e-print-checklist { display: flex; flex-wrap: wrap; gap: 6px 18px; }
    .e-print-checklist-vertical { flex-direction: column; gap: 6px; }
    .e-print-check-item { display: inline-flex; align-items: center; gap: 7px; font-size: 1rem; }
    .e-print-checkbox { width: 16px; height: 16px; border: 1.5px solid #444; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; line-height: 1; flex-shrink: 0; }
    .e-print-checkbox.is-checked { border-color: #141414; font-weight: bold; }
    .e-print-section { margin: 18px 0 10px; border-bottom: 1px solid #d2d2d2; padding-bottom: 5px; }
    .e-print-section-title { font-size: 1.2rem; font-weight: 700; }
    .e-print-section-desc { font-size: 0.85rem; color: #6b7280; margin-top: 3px; }
    .e-print-field-block { padding: 10px 0; }
    .e-print-field-title { font-weight: 700; font-size: 1rem; margin-bottom: 5px; }
    .e-print-grid-table { width: 100%; border-collapse: collapse; font-size: 0.95rem; }
    .e-print-grid-table th, .e-print-grid-table td { border: 1px solid #d2d2d2; padding: 7px 8px; text-align: left; }
    .e-print-grid-table th { background: #f5f7fb; font-weight: 700; }
    .e-print-meta-box { border: 1px solid #d2d2d2; padding: 10px 12px; margin-top: 20px; font-size: 0.85rem; color: #3c3c3c; }
    .e-print-meta-box div + div { margin-top: 4px; }
    .e-print-ref { font-size: 0.75rem; color: #a03c32; margin-top: 12px; }
    @media print {
        body { padding: 8mm 12mm; }
        @page { margin: 10mm; }
    }
`

/**
 * Resolves the app name to show at the top of the printout.
 * Priority: explicit meta.appName override, then localStorage["app_name"].
 */
const getAppName = (meta) => {
    if (meta.appName) return meta.appName
    try {
        return typeof localStorage !== "undefined" ? localStorage.getItem("app_name") : null
    } catch {
        return null
    }
}

/**
 * Opens the browser's native print dialog with a printable snapshot of the
 * form. Mirrors the layout of formPdfBuilder.js's generateFormPdf but renders
 * HTML into a new tab instead of a downloaded .pdf file.
 *
 * Implementation note: this uses a Blob object URL (window.open(blobUrl))
 * rather than window.open("") + document.write(). document.write() opens a
 * blank tab first and paints the real content a moment later, which is the
 * "main page loads first, then the popup/content shows" flash. Navigating
 * straight to a blob URL renders the finished document immediately, same as
 * opening any other URL in a new tab - no blank intermediate state.
 *
 * The print trigger also moves *into* the printed document itself (a small
 * inline script that calls window.print() on window.onload). That's more
 * reliable than polling the logo <img> from the opener window, since
 * window.onload already waits for every image/resource to finish loading.
 */
export const printForm = ({ formItem, formValues = {}, meta = {}, translate }) => {
    const appName = getAppName(meta)
    const logoSrc = meta.logoUrl || null

    const headerHtml = `
        <div class="e-print-header">
        ${logoSrc ? `<img class="e-print-logo" src="${esc(logoSrc)}" />` : ""}
            ${appName ? `<div class="e-print-app-name">${esc(appName)}</div>` : ""}
            ${meta.orgName ? `<div class="e-print-org-name">${esc(meta.orgName)}</div>` : ""}
            <div class="e-print-title">${esc(meta.formTitle || formItem?.title_key || t(translate, "Form Details"))}</div>
            ${
                meta.patientName
                    ? `<div class="e-print-patient">${esc(t(translate, "Patient"))}: <strong>${esc(
                          meta.patientName,
                      )}</strong></div>`
                    : ""
            }
            ${meta.date ? `<div class="e-print-date">${esc(t(translate, "Date"))}: ${esc(meta.date)}</div>` : ""}
        </div>
        <hr class="e-print-divider" />
    `

    const bodyHtml = renderFieldRows(formItem, formValues, translate)

    const footerLines = []
    if (meta.filledByName) footerLines.push(`${t(translate, "Filled by")}: ${meta.filledByName}`)
    if (meta.filledDate) footerLines.push(`${t(translate, "Date")}: ${meta.filledDate}`)
    if (meta.lastUpdatedDate) footerLines.push(`${t(translate, "Last updated")}: ${meta.lastUpdatedDate}`)
    const footerHtml = footerLines.length
        ? `<div class="e-print-meta-box">${footerLines.map((l) => `<div>${esc(l)}</div>`).join("")}</div>`
        : ""

    const refHtml = meta.refNumber
        ? `<div class="e-print-ref">${esc(t(translate, "Ref"))}: ${esc(meta.refNumber)}</div>`
        : ""

    const documentTitle = meta.formTitle || formItem?.title_key || t(translate, "Form Details")

    const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>${esc(documentTitle)}</title>
    <style>${PRINT_STYLES}</style>
</head>
<body>
    ${headerHtml}
    ${bodyHtml}
    ${footerHtml}
    ${refHtml}
    <script>
        window.onload = function () {
            window.print();
        };
    </script>
</body>
</html>`

    const blob = new Blob([html], { type: "text/html" })
    const blobUrl = URL.createObjectURL(blob)

    // Must happen synchronously in the click handler (no await/promise before
    // this point) or popup blockers will swallow it.
    const printWindow = window.open(blobUrl, "_blank")

    if (!printWindow) {
        URL.revokeObjectURL(blobUrl)
        throw new Error("Popup blocked - please allow popups for this site to print the form.")
    }

    // Release the blob once the document (and its auto-print script) has
    // loaded - keep a generous fallback in case the load event doesn't fire
    // (e.g. the tab was closed immediately).
    const revoke = () => URL.revokeObjectURL(blobUrl)
    try {
        printWindow.addEventListener("load", revoke, { once: true })
    } catch {
        // Cross-window listener can occasionally be restricted; the fallback
        // timeout below still cleans it up.
    }
    setTimeout(revoke, 15000)

    return { printWindow }
}